var App = App || {};
App.Initialization = App.Initialization || {};

App.Initialization['component.primaryNav'] = (function () {
    'use strict';

    // ========================================================================
    // Configuration
    // ========================================================================

    const CONFIG = {
        SELECTOR_NAV: 'nav.primaryNav',
        SELECTOR_TRIGGERS: 'ul.level-1 > li > a, ul.level-1 > li > button',
        SELECTOR_MENUS: '.primaryNav__megaMenu',
        CLASS_OPEN: 'mega-menu-open',
    };

    // ========================================================================
    // State
    // ========================================================================

    const State = {
        mmActive: false,
        mmActiveItems: [],
        mmActiveItem: 0,
    };

    // ========================================================================
    // Initialization
    // ========================================================================

    function initialize(nav) {
        const triggers = nav.querySelectorAll(CONFIG.SELECTOR_TRIGGERS);
        const megaMenuLinks = nav.querySelectorAll(`${CONFIG.SELECTOR_MENUS} a`);

        // Keep mega-menu links out of natural tab order while closed.
        megaMenuLinks.forEach(link => link.setAttribute('tabindex', '-1'));

        Events.bindContainerEvents(nav, triggers);
        Events.bindTriggerEvents(triggers);
        Events.bindMegaMenuLinkEvents(nav, triggers);
    }

    // ========================================================================
    // Event Binding
    // ========================================================================

    const Events = {

        bindContainerEvents(nav, triggers) {
            // Leaving the nav closes everything
            nav.addEventListener('mouseleave', () => {
                State.mmActive = false;
                Actions.closeAll(triggers);
            });

            // Tab key leaves nav — close everything
            nav.addEventListener('keydown', (e) => {
                if (e.key === 'Tab') {
                    State.mmActive = false;
                    Actions.closeAll(triggers);
                }
                if (e.key === 'Escape') {
                    State.mmActive = false;
                    Actions.closeAll(triggers);
                }
            });
        },

        bindTriggerEvents(triggers) {
            triggers.forEach(trigger => {
                trigger.addEventListener('mouseenter', () => {
                    Actions.open(trigger, triggers);
                    State.mmActive = true;
                });

                trigger.addEventListener('focus', () => {
                    Actions.open(trigger, triggers);
                    State.mmActive = true;
                });

                trigger.addEventListener('blur', () => {
                    if (State.mmActive === false) {
                        Actions.close(trigger);
                    }
                });

                trigger.addEventListener('keydown', (e) => {
                    Events.handleTriggerKeyDown(e, trigger, triggers);
                });
            });
        },

        bindMegaMenuLinkEvents(nav, triggers) {
            const megaMenuLinks = nav.querySelectorAll(`${CONFIG.SELECTOR_MENUS} a, ${CONFIG.SELECTOR_MENUS} button`);

            megaMenuLinks.forEach(link => {
                link.addEventListener('keydown', (e) => {
                    Events.handleMenuLinkKeyDown(e, link, triggers);
                });
            });
        },

        handleTriggerKeyDown(e, trigger, triggers) {
            const li = trigger.parentElement;
            const megaMenu = li.querySelector(CONFIG.SELECTOR_MENUS);
            const megaMenuLinks = megaMenu ? [...megaMenu.querySelectorAll('a, button')] : [];

            switch (e.key) {
                case 'ArrowRight':
                    e.preventDefault();
                    Helpers.focusSiblingTrigger(trigger, triggers, 'next');
                    break;
                case 'ArrowLeft':
                    e.preventDefault();
                    Helpers.focusSiblingTrigger(trigger, triggers, 'prev');
                    break;
                case 'ArrowDown':
                    e.preventDefault();
                    State.mmActive = true;
                    State.mmActiveItems = megaMenuLinks;
                    State.mmActiveItem = 0;
                    if (megaMenuLinks.length) megaMenuLinks[0].focus();
                    break;
            }
        },

        handleMenuLinkKeyDown(e, link, triggers) {
            const megaMenu = link.closest(CONFIG.SELECTOR_MENUS);
            const megaMenuLinks = megaMenu ? [...megaMenu.querySelectorAll('a, button')] : [];
            const currentIdx = megaMenuLinks.indexOf(link);
            const parentTrigger = megaMenu?.parentElement?.querySelector(CONFIG.SELECTOR_TRIGGERS);

            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    State.mmActive = true;
                    State.mmActiveItem = currentIdx < megaMenuLinks.length - 1 ? currentIdx + 1 : 0;
                    megaMenuLinks[State.mmActiveItem].focus();
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    State.mmActive = true;
                    State.mmActiveItem = currentIdx > 0 ? currentIdx - 1 : megaMenuLinks.length - 1;
                    megaMenuLinks[State.mmActiveItem].focus();
                    break;
                case 'ArrowRight':
                    e.preventDefault();
                    State.mmActive = true;
                    if (parentTrigger) Helpers.focusSiblingTrigger(parentTrigger, triggers, 'next');
                    break;
                case 'ArrowLeft':
                    e.preventDefault();
                    State.mmActive = true;
                    if (parentTrigger) Helpers.focusSiblingTrigger(parentTrigger, triggers, 'prev');
                    break;
                case 'Escape':
                    e.preventDefault();
                    if (parentTrigger) {
                        Actions.closeAll(triggers);
                        parentTrigger.focus();
                    }
                    break;
            }
        }
    };

    // ========================================================================
    // Backdrop
    // ========================================================================

    const Backdrop = {
        _el: null,

        show() {
            if (this._el) return;
            const el = document.createElement('div');
            el.className = 'primaryNav__backdrop';
            document.body.appendChild(el);
            el.offsetHeight; // force reflow to enable CSS transition
            el.classList.add('show');
            this._el = el;
        },

        hide() {
            if (!this._el) return;
            const el = this._el;
            this._el = null;
            el.classList.remove('show');
            el.addEventListener('transitionend', () => el.remove(), { once: true });
        }
    };

    // ========================================================================
    // Actions
    // ========================================================================

    const Actions = {

        open(trigger, triggers) {
            const allTriggers = [...triggers];
            const otherTriggers = allTriggers.filter(t => t !== trigger);

            // Close all others first so only one panel is open at a time.
            otherTriggers.forEach(t => {
                Actions.close(t);
                t.blur();
            });

            trigger.parentElement.classList.add(CONFIG.CLASS_OPEN);
            trigger.setAttribute('aria-expanded', 'true');
            Helpers.setTabIndex(trigger.parentElement, true);
            Backdrop.show();
        },

        close(trigger) {
            trigger.parentElement.classList.remove(CONFIG.CLASS_OPEN);
            trigger.setAttribute('aria-expanded', 'false');
            Helpers.setTabIndex(trigger.parentElement, false);
        },

        closeAll(triggers) {
            [...triggers].forEach(t => Actions.close(t));
            Backdrop.hide();
        }
    };

    // ========================================================================
    // Helpers
    // ========================================================================

    const Helpers = {

        setTabIndex(li, open) {
            const menu = li.querySelector(CONFIG.SELECTOR_MENUS);
            if (!menu) return;
            menu.querySelectorAll('a, button').forEach(item => {
                item.setAttribute('tabindex', open ? '0' : '-1');
            });
        },

        focusSiblingTrigger(trigger, triggers, direction) {
            const list = [...triggers];
            const idx = list.indexOf(trigger);
            const next = direction === 'next'
                ? (idx + 1) % list.length
                : (idx - 1 + list.length) % list.length;
            list[next].focus();
        }
    };


    return {
        initialize
    };

})();
